import { useEffect, useRef, useState } from 'react'
import {
  Modal,
  Form,
  Select,
  Button,
  Table,
  Tag,
  Space,
  Typography,
  message,
  Alert,
  Row,
  Col,
  Empty,
  Tooltip,
  Drawer,
  Descriptions,
} from 'antd'
import { PlusOutlined, DeleteOutlined, DownloadOutlined, CopyOutlined } from '@ant-design/icons'
import { connect } from 'react-redux'
import configuration from 'modules/configuration'
import planning from 'modules/planning'

const SLOT_CODE = {
  company: 'sociedad',
  cost_center: 'ceco',
  ap: 'ap',
}

const SLOT_LABELS = {
  company: 'Sociedad',
  cost_center: 'Centro de Costo',
  ap: 'AP',
}

const emptyKey = () => ({
  company: undefined,
  cost_center: undefined,
  ap: undefined,
})

const DETAIL_PAGE_SIZE = 20

const emptyDetail = () => ({
  visible: false,
  row: null,
  rows: [],
  total: 0,
  page: 1,
  loading: false,
})

const formatAmount = (value) =>
  value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const availableColor = (value) => (value < 0 ? 'error' : value === 0 ? 'default' : 'success')

const formatToday = () =>
  new Date().toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

const buildOptionLabel = (value) => {
  const code = (value.code_destination || '').trim()
  const description = (value.name_destination || value.name || '').trim()

  if (code && description) return `${code} - ${description}`
  if (code) return code
  if (description) return description
  return ''
}

const findDimensionFor = (slot, dimensions) => {
  const target = SLOT_CODE[slot].trim().toLowerCase()
  return dimensions.find((d) => (d.name || '').trim().toLowerCase() === target)
}

const firstString = (value) => {
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (Array.isArray(value)) return firstString(value[0])
  return null
}

const errorMessage = (error, fallback) => {
  const data = error?.response?.data
  return firstString(data) ?? firstString(data?.error) ?? firstString(data?.detail) ?? fallback
}

const extractDimensions = (response) => {
  const payload = response?.payload?.data
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

function CheckBalanceModal({
  open,
  onClose,
  id,
  fetchAllDimensions,
  fetchAllDimensionsValues,
  getAvailableBalance,
  getAvailableBalanceDetail,
}) {
  const [dimensions, setDimensions] = useState([])
  const [keys, setKeys] = useState([emptyKey()])
  const [loadingData, setLoadingData] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [rows, setRows] = useState([])
  const [hasQueried, setHasQueried] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const [detail, setDetail] = useState(emptyDetail())
  const detailRequestId = useRef(0)
  const [destinationValues, setDestinationValues] = useState({
    company: [],
    cost_center: [],
    ap: [],
  })

  const dimensionFor = (slot) => findDimensionFor(slot, dimensions)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoadingData(true)
    fetchAllDimensions()
      .then((response) => {
        if (!cancelled) setDimensions(extractDimensions(response))
      })
      .catch(() => {
        if (!cancelled) setDimensions([])
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, fetchAllDimensions])

  useEffect(() => {
    if (!open || dimensions.length === 0) return
    let cancelled = false
    const slots = ['company', 'cost_center', 'ap']
    const tasks = slots.map(async (slot) => {
      const dim = dimensionFor(slot)
      const dimId = dim?.id ?? dim?.id_dimension
      if (dimId == null) return [slot, []]
      try {
        const response = await fetchAllDimensionsValues({ id_dimension: dimId })
        const data = response?.payload?.data ?? []
        return [slot, data]
      } catch {
        return [slot, []]
      }
    })
    Promise.all(tasks).then((results) => {
      if (cancelled) return
      setDestinationValues((prev) => {
        const next = { ...prev }
        for (const [slot, data] of results) next[slot] = data
        return next
      })
    })
    return () => {
      cancelled = true
    }
  }, [open, dimensions, fetchAllDimensionsValues])

  const missingSlots = ['company', 'cost_center', 'ap'].filter((slot) => !dimensionFor(slot))

  const hasIncompleteKey = keys.some((k) => !k.company || !k.cost_center || !k.ap)

  const handleKeyChange = (index, slot, value) => {
    setKeys((prev) => prev.map((k, i) => (i === index ? { ...k, [slot]: value } : k)))
  }

  const handleAddKey = () => {
    setKeys((prev) => [...prev, emptyKey()])
  }

  const handleRemoveKey = (index) => {
    setKeys((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)))
  }

  const handleDuplicateKey = (index) => {
    setKeys((prev) => [...prev.slice(0, index + 1), { ...prev[index] }, ...prev.slice(index + 1)])
  }

  const handleReset = () => {
    setKeys([emptyKey()])
    setRows([])
    setHasQueried(false)
    setShowErrors(false)
    detailRequestId.current += 1
    setDetail(emptyDetail())
  }

  const handleOpenDetail = async (row, page) => {
    const requestId = ++detailRequestId.current
    setDetail((prev) => ({ ...prev, visible: true, row, page, loading: true }))
    try {
      const response = await getAvailableBalanceDetail({
        overall_budget_id: id,
        company_code: row.company_code,
        cost_center_code: row.cost_center_code,
        ap_code: row.ap_code,
        page,
        page_size: DETAIL_PAGE_SIZE,
      })
      if (requestId !== detailRequestId.current) return
      const data = response.payload.data
      setDetail((prev) => ({
        ...prev,
        rows: data.results || [],
        total: data.count || 0,
        loading: false,
      }))
    } catch (error) {
      if (requestId !== detailRequestId.current) return
      message.error(errorMessage(error, 'No se pudo consultar el detalle del comprometido'))
      setDetail((prev) => ({ ...prev, rows: [], total: 0, loading: false }))
    }
  }

  const handleCloseDetail = () => {
    detailRequestId.current += 1
    setDetail(emptyDetail())
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  const handleSubmit = async () => {
    const cleanedKeys = keys.map((k) => ({
      company_code: (k.company || '').trim(),
      cost_center_code: (k.cost_center || '').trim(),
      ap_code: (k.ap || '').trim(),
    }))

    const incompleteIdx = cleanedKeys.findIndex(
      (k) => !k.company_code || !k.cost_center_code || !k.ap_code,
    )
    if (incompleteIdx !== -1) {
      setShowErrors(true)
      message.error(`La clave #${incompleteIdx + 1} tiene campos vacíos`)
      return
    }

    setShowErrors(false)
    setSubmitting(true)
    try {
      const response = await getAvailableBalance({
        overall_budget_id: id,
        keys: cleanedKeys,
      })
      setRows(response.payload.data.rows || [])
      setHasQueried(true)
    } catch (error) {
      message.error(errorMessage(error, 'No se pudo consultar el saldo disponible'))
    } finally {
      setSubmitting(false)
    }
  }

  const renderSlotSelect = (index, slot) => {
    const options = destinationValues[slot]
      .map((v) => ({
        value: v.code_destination ?? '',
        label: buildOptionLabel(v),
      }))
      .filter((o) => o.value)
    return (
      <Select
        showSearch
        allowClear
        loading={loadingData}
        className={showErrors && !keys[index][slot] ? 'ant-select-status-error' : ''}
        placeholder={SLOT_LABELS[slot]}
        style={{ width: '100%' }}
        value={keys[index][slot]}
        onChange={(value) => handleKeyChange(index, slot, value)}
        options={options}
        notFoundContent={loadingData ? 'Cargando...' : 'Sin opciones'}
        filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
      />
    )
  }

  const nameBySlot = (slot, code) => {
    if (!code) return code
    const match = destinationValues[slot].find((v) => v.code_destination === code)
    return match?.name_destination ?? match?.name ?? code
  }

  const handleExport = () => {
    if (rows.length === 0) return

    const headers = [
      'Compañía',
      'Centro de Costo',
      'AP',
      'Presupuesto',
      'Comprometido',
      'Disponible',
    ]
    const escapeCell = (value) => {
      const text = String(value ?? '')
      return /[";\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
    }

    const dataLines = rows.map((r) =>
      [
        nameBySlot('company', r.company_code),
        nameBySlot('cost_center', r.cost_center_code),
        nameBySlot('ap', r.ap_code),
        formatAmount(r.budget),
        formatAmount(r.committed),
        formatAmount(r.available),
      ]
        .map(escapeCell)
        .join(';'),
    )

    const totals = rows.reduce(
      (acc, r) => ({
        budget: acc.budget + (r.budget || 0),
        committed: acc.committed + (r.committed || 0),
        available: acc.available + (r.available || 0),
      }),
      { budget: 0, committed: 0, available: 0 },
    )
    const totalsLine = [
      'Totales',
      '',
      '',
      formatAmount(totals.budget),
      formatAmount(totals.committed),
      formatAmount(totals.available),
    ]
      .map(escapeCell)
      .join(';')

    const csv = [headers.join(';'), ...dataLines, totalsLine].join('\r\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const today = new Date().toLocaleDateString('es-AR').replace(/\//g, '-')
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `saldo-disponible-${today}.csv`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  const columns = [
    {
      title: 'Compañía',
      dataIndex: 'company_code',
      key: 'company_code',
      render: (val) => nameBySlot('company', val),
    },
    {
      title: 'Centro de Costo',
      dataIndex: 'cost_center_code',
      key: 'cost_center_code',
      render: (val) => nameBySlot('cost_center', val),
    },
    {
      title: 'AP',
      dataIndex: 'ap_code',
      key: 'ap_code',
      render: (val) => nameBySlot('ap', val),
    },
    {
      title: 'Presupuesto',
      dataIndex: 'budget',
      key: 'budget',
      align: 'right',
      render: (val) => formatAmount(val),
    },
    {
      title: 'Comprometido',
      dataIndex: 'committed',
      key: 'committed',
      align: 'right',
      render: (val, row) =>
        val ? (
          <Tooltip title="Ver detalle del comprometido">
            <Button
              type="link"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => handleOpenDetail(row, 1)}
            >
              {formatAmount(val)}
            </Button>
          </Tooltip>
        ) : (
          formatAmount(val)
        ),
    },
    {
      title: 'Disponible',
      dataIndex: 'available',
      key: 'available',
      align: 'right',
      render: (val) => (
        <Tag color={availableColor(val)} style={{ fontWeight: 500 }}>
          {formatAmount(val)}
        </Tag>
      ),
    },
  ]

  const detailColumns = [
    { title: 'Año', dataIndex: 'year', key: 'year' },
    { title: 'Mes', dataIndex: 'month', key: 'month' },
    { title: 'Tipo de documento', dataIndex: 'document_type', key: 'document_type' },
    { title: 'Moneda', dataIndex: 'currency', key: 'currency' },
    {
      title: 'Importe',
      dataIndex: 'consumed_amount',
      key: 'consumed_amount',
      align: 'right',
      render: (val) => formatAmount(Number(val)),
    },
  ]

  return (
    <>
      <Modal
        title="Consultar Saldo Disponible"
        visible={open}
        onCancel={handleClose}
        width={1080}
        footer={[
          <Button key="close" onClick={handleClose}>
            Cerrar
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitting}
            onClick={handleSubmit}
            disabled={loadingData || hasIncompleteKey}
          >
            Consultar
          </Button>,
        ]}
      >
        {missingSlots.length > 0 && !loadingData && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="No se encontraron las dimensiones necesarias"
            description={`Faltan dimensiones para: ${missingSlots
              .map((s) => SLOT_LABELS[s])
              .join(', ')}`}
          />
        )}

        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Saldo al {formatToday()}
        </Typography.Text>

        <Form layout="vertical">
          <Form.Item label="Claves" required>
            <Space direction="vertical" style={{ width: '100%' }}>
              {keys.map((_k, idx) => (
                <Row key={idx} gutter={8} align="middle" wrap={false} style={{ width: '100%' }}>
                  <Col flex="1 1 0">{renderSlotSelect(idx, 'company')}</Col>
                  <Col flex="1 1 0">{renderSlotSelect(idx, 'cost_center')}</Col>
                  <Col flex="1 1 0">{renderSlotSelect(idx, 'ap')}</Col>
                  <Col flex="0 0 auto">
                    <Tooltip title="Duplicar clave">
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => handleDuplicateKey(idx)}
                        aria-label="Duplicar clave"
                      />
                    </Tooltip>
                    <Tooltip title="Eliminar clave">
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveKey(idx)}
                        disabled={keys.length <= 1}
                        aria-label="Eliminar clave"
                      />
                    </Tooltip>
                  </Col>
                </Row>
              ))}
              <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddKey}>
                Agregar clave
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {rows.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Exportar
            </Button>
          </div>
        )}

        {rows.length > 0 && (
          <Table
            columns={columns}
            dataSource={rows}
            rowKey={(_r, i) => i ?? 0}
            pagination={false}
            size="small"
            bordered
            scroll={{ x: 'max-content' }}
            style={{ marginTop: 8 }}
            summary={(data) => {
              const totals = data.reduce(
                (acc, r) => ({
                  budget: acc.budget + (r.budget || 0),
                  committed: acc.committed + (r.committed || 0),
                  available: acc.available + (r.available || 0),
                }),
                { budget: 0, committed: 0, available: 0 },
              )
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <strong>Totales</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <strong>{formatAmount(totals.budget)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <strong>{formatAmount(totals.committed)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      <Tag color={availableColor(totals.available)} style={{ fontWeight: 600 }}>
                        {formatAmount(totals.available)}
                      </Tag>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )
            }}
          />
        )}

        {hasQueried && rows.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No se encontraron saldos para las claves consultadas"
            style={{ marginTop: 24 }}
          />
        )}
      </Modal>

      <Drawer
        title="Detalle del comprometido"
        placement="right"
        width={720}
        zIndex={1100}
        visible={detail.visible}
        onClose={handleCloseDetail}
      >
        {detail.row && (
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Compañía">
              {nameBySlot('company', detail.row.company_code)}
            </Descriptions.Item>
            <Descriptions.Item label="Centro de Costo">
              {nameBySlot('cost_center', detail.row.cost_center_code)}
            </Descriptions.Item>
            <Descriptions.Item label="AP">{nameBySlot('ap', detail.row.ap_code)}</Descriptions.Item>
            <Descriptions.Item label="Comprometido">
              <strong>{formatAmount(detail.row.committed)}</strong>
            </Descriptions.Item>
          </Descriptions>
        )}

        <Table
          columns={detailColumns}
          dataSource={detail.rows}
          rowKey={(r) => r.id}
          loading={detail.loading}
          size="small"
          bordered
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Este comprometido no tiene filas de detalle' }}
          pagination={{
            current: detail.page,
            pageSize: DETAIL_PAGE_SIZE,
            total: detail.total,
            showSizeChanger: false,
            showTotal: (total) => `${total} registros`,
            onChange: (page) => handleOpenDetail(detail.row, page),
          }}
        />
      </Drawer>
    </>
  )
}

const mapStateToProps = () => ({})

const mapDispatchToProps = {
  fetchAllDimensions: configuration.actions.fetchAllDimensions,
  fetchAllDimensionsValues: configuration.actions.fetchAllDimensionsValues,
  getAvailableBalance: planning.actions.getAvailableBalance,
  getAvailableBalanceDetail: planning.actions.getAvailableBalanceDetail,
}

export default connect(mapStateToProps, mapDispatchToProps)(CheckBalanceModal)
